import React, { useRef, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Textarea,
  Text,
  Alert,
  AlertIcon,
  VStack,
  HStack,
  Box,
  Icon,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiUpload, FiFile, FiCopy } from 'react-icons/fi';
import { AI_CAMPAIGN_PROMPT } from './campaignImportPrompt';

/**
 * ImportCampaignModal
 *
 * Lets a user load a previously exported campaign into the builder, either by
 * pasting the JSON or uploading a .json file. Parsing/validation errors are
 * shown inline so the user can correct and retry. The actual application of the
 * data (and UUID remapping) happens in the parent via `onImport(parsedObject)`.
 */
export default function ImportCampaignModal({ isOpen, onClose, onImport, hasExistingNodes = false }) {
  const toast = useToast();
  const fileInputRef = useRef(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [pasteText, setPasteText] = useState('');
  const [fileContent, setFileContent] = useState('');
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const codeBg = useColorModeValue('gray.50', 'gray.800');
  const dividerColor = useColorModeValue('gray.100', 'gray.700');

  const resetAll = () => {
    setPasteText('');
    setFileContent('');
    setFileName('');
    setError('');
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  const handleFileChange = async (e) => {
    setError('');
    const file = e.target.files?.[0];
    // Allow re-picking the same file later
    e.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      setFileContent(text);
      setFileName(file.name);
    } catch (err) {
      setError(`Could not read file: ${err.message || 'unknown error'}`);
    }
  };

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(AI_CAMPAIGN_PROMPT);
      toast({ title: 'AI prompt copied to clipboard', status: 'success', duration: 2000, isClosable: true });
    } catch {
      toast({ title: 'Copy failed', description: 'Clipboard not available', status: 'error', duration: 3000, isClosable: true });
    }
  };

  const handleImport = () => {
    setError('');
    const raw = tabIndex === 0 ? pasteText : fileContent;
    if (!raw || !raw.trim()) {
      setError(tabIndex === 0 ? 'Paste the campaign JSON first.' : 'Choose a .json file first.');
      return;
    }

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      setError(`That is not valid JSON. ${e.message || ''}`.trim());
      return;
    }

    setBusy(true);
    try {
      onImport(parsed);
      resetAll();
    } catch (e) {
      setError(e.message || 'Import failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Import Campaign</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <Text fontSize="sm" color="gray.500">
              Paste exported campaign JSON or upload a <Box as="span" fontWeight="semibold">.json</Box> file.
              Importing loads the trigger and all steps into this campaign so you can review and Save.
            </Text>

            {hasExistingNodes && (
              <Alert status="warning" borderRadius="md" fontSize="sm">
                <AlertIcon />
                This campaign already has steps. Importing replaces everything currently in the builder.
              </Alert>
            )}

            {error && (
              <Alert status="error" borderRadius="md" fontSize="sm">
                <AlertIcon />
                {error}
              </Alert>
            )}

            <Tabs index={tabIndex} onChange={setTabIndex} variant="enclosed" colorScheme="blue" size="sm">
              <TabList>
                <Tab>Paste JSON</Tab>
                <Tab>Upload File</Tab>
              </TabList>
              <TabPanels>
                <TabPanel px={0}>
                  <Textarea
                    value={pasteText}
                    onChange={(e) => { setPasteText(e.target.value); if (error) setError(''); }}
                    placeholder='{ "template": { ... }, "nodes": [ ... ] }'
                    rows={12}
                    fontFamily="mono"
                    fontSize="xs"
                    bg={codeBg}
                  />
                </TabPanel>
                <TabPanel px={0}>
                  <VStack align="stretch" spacing={3} py={2}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="application/json,.json"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    <Button
                      leftIcon={<Icon as={FiUpload} />}
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Choose .json file
                    </Button>
                    {fileName && (
                      <HStack fontSize="sm" color="gray.500">
                        <Icon as={FiFile} />
                        <Text>{fileName}</Text>
                      </HStack>
                    )}
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>

            <Box pt={1} borderTopWidth="1px" borderColor={dividerColor}>
              <HStack justify="space-between" align="center" pt={3}>
                <Text fontSize="xs" color="gray.500">
                  Want an AI to build a campaign for you? Copy the prompt, paste it into any AI with your
                  description, then import the JSON it returns.
                </Text>
                <Button
                  size="xs"
                  variant="ghost"
                  leftIcon={<Icon as={FiCopy} />}
                  onClick={handleCopyPrompt}
                  flexShrink={0}
                >
                  Copy AI prompt
                </Button>
              </HStack>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleImport} isLoading={busy}>
            Import
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
